import type { FileOpenSurface } from '../fileBrowser/fileTypeRegistry';
import { displayNameFromPath } from '../mediaViewer/mediaFileTypes';

export type EditorTabKind =
  | 'text'
  | 'code'
  | 'markdown'
  | 'drawio'
  | 'document'
  | 'spreadsheet'
  | 'pdf'
  | 'media';

const EDITOR_TAB_KINDS = new Set<EditorTabKind>([
  'text',
  'code',
  'markdown',
  'drawio',
  'document',
  'spreadsheet',
  'pdf',
  'media'
]);

export type EditorWorkspacePane = 'primary' | 'secondary';
export type EditorAccessMode = 'edit' | 'view';

export type EditorTab = {
  id: string;
  kind: EditorTabKind;
  mountId: string;
  path: string;
  title: string;
  dirty?: boolean;
  pane: EditorWorkspacePane;
  /** Whether the Workspace surface may mutate the file. */
  accessMode: EditorAccessMode;
  /** Media viewer subtype when kind is `media`. */
  mediaKind?: 'image' | 'video';
};

const TAB_ID_SEP = ':';

export function buildEditorTabId(kind: EditorTabKind, mountId: string, path: string): string {
  return [kind, mountId, path].join(TAB_ID_SEP);
}

export function parseEditorTabId(tabId: string): Pick<EditorTab, 'kind' | 'mountId' | 'path'> | null {
  const firstSep = tabId.indexOf(TAB_ID_SEP);
  if (firstSep < 0) return null;
  const secondSep = tabId.indexOf(TAB_ID_SEP, firstSep + 1);
  if (secondSep < 0) return null;

  const kind = tabId.slice(0, firstSep) as EditorTabKind;
  const mountId = tabId.slice(firstSep + 1, secondSep);
  const path = tabId.slice(secondSep + 1);
  if (!EDITOR_TAB_KINDS.has(kind) || !mountId || !path) return null;

  return { kind, mountId, path };
}

export function surfaceToEditorTabKind(surface: FileOpenSurface): EditorTabKind | null {
  switch (surface) {
    case 'monaco':
      return 'text';
    case 'code':
      return 'code';
    case 'markdown':
      return 'markdown';
    case 'drawio':
      return 'drawio';
    case 'richedit':
      return 'document';
    case 'spreadsheet':
      return 'spreadsheet';
    case 'pdf':
      return 'pdf';
    case 'media':
      return 'media';
    default:
      return null;
  }
}

export function defaultTabTitle(path: string): string {
  return displayNameFromPath(path);
}
